import { Popover, Progress, Stack } from '@inflearn/ds-react';
import type { PopoverProps } from '@mantine/core';
import type { ReactNode } from 'react';

import { groupBy } from 'lodash-es';
import type { ValidationMessage } from '../../../utils/validation';
import { ValidationItem } from './ValidationItem';

type ValidationPopoverProps<T = string> = {
  children: ReactNode;
  opened: boolean;
  validationMessages: ValidationMessage<T>[];
  position?: PopoverProps['position'];
  withinPortal?: boolean;
};

/**
 * Input 컴포넌트에 validation 피드백을 제공하는 Popover 컴포넌트
 * Focus 시 validation 상태를 시각적으로 표시합니다.
 */
export const ValidationPopover = <T = string>({
  children,
  opened,
  validationMessages,
  position = 'bottom-start',
  withinPortal = true,
}: ValidationPopoverProps<T>) => {
  const { error: errorMessage = [], success: successMessage = [] } = groupBy(
    validationMessages,
    'status',
  );

  const errorCount = errorMessage.length;
  const successCount = successMessage.length;
  const totalCount = validationMessages.length;
  const progressValue = successCount > 0 ? ((totalCount - errorCount) / totalCount) * 100 : 0;

  const hasError = errorCount > 0;
  return (
    <Popover
      opened={opened}
      position={position}
      trapFocus={false}
      closeOnEscape={false}
      closeOnClickOutside={false}
      shadow="md"
      withinPortal={withinPortal}
      width="target"
    >
      <Popover.Target>{children}</Popover.Target>
      <Popover.Dropdown py={12} px={16}>
        <Stack spacing={8}>
          <Progress
            value={progressValue}
            color={hasError ? 'red.6' : 'infgreen.6'}
            size="sm"
            radius="xl"
            style={{ height: '4px' }}
          />

          <Stack spacing={4}>
            {validationMessages.map(({ type, message, status }) => (
              <ValidationItem key={String(type)} isValid={status === 'success'} text={message} />
            ))}
          </Stack>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
